import {
  BasicTransformEvent,
  Canvas,
  FabricObject,
  Line,
  Point,
  TPointerEvent,
  TPointerEventInfo,
} from 'fabric';

import {
  SNAP_LINE_COLOR,
  SNAP_THRESHOLD,
  PENDING_SNAP_LINE_COLOR,
  SHOW_THRESHOLD,
} from './constants';

import { getObjectSnapPoints, getSnappableObjects, SnapLineEnum } from './helpers';

// Types
interface ResizeEdges {
  horizontal: number; // -1: not resizing, 0: left, 2: right
  vertical: number; // -1: not resizing, 0: top, 2: bottom
}

interface SnapPoint {
  value: number;
  edge: number;
  sourceObj: FabricObject;
  distance: number;
}

interface PendingSnap {
  value: number;
  edge: number;
  direction: 'horizontal' | 'vertical';
}

interface SnapLineEndpoints {
  start: Point;
  end: Point;
}

// State
const state = {
  activeResizeSnapLines: [] as Line[],
  pendingResizeSnap: null as PendingSnap | null,
  activeResizeCorner: null as string | null,
};

/**
 * Calculate which edges are being resized based on the active control point
 */
const getResizingEdges = (corner: string | undefined): ResizeEdges | null => {
  if (!corner) return null;

  // Map corner positions to their corresponding edges
  const cornerToEdges: Record<string, ResizeEdges> = {
    tl: { horizontal: 0, vertical: 0 },
    tr: { horizontal: 2, vertical: 0 },
    bl: { horizontal: 0, vertical: 2 },
    br: { horizontal: 2, vertical: 2 },
    ml: { horizontal: 0, vertical: -1 },
    mr: { horizontal: 2, vertical: -1 },
    mt: { horizontal: -1, vertical: 0 },
    mb: { horizontal: -1, vertical: 2 },
  };

  return cornerToEdges[corner] || null;
};

/**
 * Calculate the endpoints for a resize snap line
 */
const calculateSnapLineEndpoints = (
  isHorizontal: boolean,
  value: number,
  sourceObj: FabricObject,
  targetObj: FabricObject,
): SnapLineEndpoints => {
  // Get bounding boxes that account for rotation and scaling
  const sourceBoundingRect = sourceObj.getBoundingRect();
  const targetBoundingRect = targetObj.getBoundingRect();
  const padding = 10;

  if (isHorizontal) {
    // For horizontal lines (y-axis alignment)
    const leftmost = Math.min(sourceBoundingRect.left, targetBoundingRect.left);
    const rightmost = Math.max(
      sourceBoundingRect.left + sourceBoundingRect.width,
      targetBoundingRect.left + targetBoundingRect.width,
    );

    return {
      start: new Point(leftmost - padding, value),
      end: new Point(rightmost + padding, value),
    };
  } else {
    // For vertical lines (x-axis alignment)
    const topmost = Math.min(sourceBoundingRect.top, targetBoundingRect.top);
    const bottommost = Math.max(
      sourceBoundingRect.top + sourceBoundingRect.height,
      targetBoundingRect.top + targetBoundingRect.height,
    );

    return {
      start: new Point(value, topmost - padding),
      end: new Point(value, bottommost + padding),
    };
  }
};

/**
 * Shows a resize snap guideline
 */
const showSnapLine = (
  canvas: Canvas,
  startPoint: Point,
  endPoint: Point,
  isHorizontal: boolean,
  isPending: boolean = false,
): Line => {
  // Create new snap line
  const snapLine = new Line([startPoint.x, startPoint.y, endPoint.x, endPoint.y], {
    strokeWidth: 1,
    stroke: isPending ? PENDING_SNAP_LINE_COLOR : SNAP_LINE_COLOR,
    selectable: false,
    evented: false,
    excludeFromExport: true,
    name: isHorizontal ? SnapLineEnum.SnapLineX : SnapLineEnum.SnapLineY,
    objectCaching: false,
    zIndex: 9999999999999,
    layer: 'overlay',
  });

  canvas.add(snapLine);
  state.activeResizeSnapLines.push(snapLine);

  return snapLine;
};

/**
 * Clears all active resize snap lines
 */
const clearSnapLines = (canvas: Canvas): void => {
  state.activeResizeSnapLines.forEach((line) => {
    canvas.remove(line);
  });
  state.activeResizeSnapLines = [];
};

/**
 * Find potential snap points between target edge and object edges
 */
const findPotentialSnapPoints = (
  targetPoints: number[],
  objPoints: number[],
  object: FabricObject,
  resizingEdgeIndex: number,
  threshold: number,
): SnapPoint[] => {
  if (resizingEdgeIndex < 0 || resizingEdgeIndex >= targetPoints.length) return [];

  const targetPoint = targetPoints[resizingEdgeIndex];

  return objPoints
    .map((objPoint) => {
      const distance = Math.abs(targetPoint - objPoint);
      return distance < threshold
        ? {
            value: objPoint,
            edge: resizingEdgeIndex,
            sourceObj: object,
            distance,
          }
        : null;
    })
    .filter((snap): snap is SnapPoint => snap !== null);
};

/**
 * Find the best snap point from a collection of potential snap points
 */
const findBestSnap = (
  potentialSnaps: SnapPoint[],
  threshold: number = SNAP_THRESHOLD,
): SnapPoint | null => {
  if (!potentialSnaps.length) return null;

  const snapsWithinThreshold = potentialSnaps.filter((snap) => snap.distance < threshold);
  if (!snapsWithinThreshold.length) return null;

  // Return the snap with the smallest distance
  return snapsWithinThreshold.sort((a, b) => a.distance - b.distance)[0];
};

/**
 * Handle snap guidelines for object resizing
 */
export const handleResizeSnap = (
  e: BasicTransformEvent<TPointerEvent> & { target: FabricObject },
  canvas: Canvas,
): boolean => {
  const { target, transform } = e;
  if (!target || !transform || !transform.corner) return false;

  const corner = transform.corner;
  state.activeResizeCorner = corner;

  const resizingEdges = getResizingEdges(corner);
  if (!resizingEdges) return false;

  const targetSnapPoints = getObjectSnapPoints(target);
  const snappableObjects = getSnappableObjects(canvas, target);

  // Clear existing snap lines and reset pending snap
  clearSnapLines(canvas);
  state.pendingResizeSnap = null;

  const potentialSnaps = {
    horizontal: [] as SnapPoint[],
    vertical: [] as SnapPoint[],
  };

  // Collect potential snap points from all snappable objects
  for (const object of snappableObjects) {
    const objPoints = getObjectSnapPoints(object);

    // Only check vertical edges if resizing horizontally
    if (resizingEdges.horizontal >= 0) {
      const verticalSnaps = findPotentialSnapPoints(
        targetSnapPoints.vertical,
        objPoints.vertical,
        object,
        resizingEdges.horizontal,
        SHOW_THRESHOLD,
      );
      potentialSnaps.vertical.push(...verticalSnaps);
    }

    // Only check horizontal edges if resizing vertically
    if (resizingEdges.vertical >= 0) {
      const horizontalSnaps = findPotentialSnapPoints(
        targetSnapPoints.horizontal,
        objPoints.horizontal,
        object,
        resizingEdges.vertical,
        SHOW_THRESHOLD,
      );
      potentialSnaps.horizontal.push(...horizontalSnaps);
    }
  }

  // Find best snap points
  const bestVerticalSnap = findBestSnap(potentialSnaps.vertical);
  const bestHorizontalSnap = findBestSnap(potentialSnaps.horizontal);

  // Draw snap lines and set pending snaps
  if (bestVerticalSnap && resizingEdges.horizontal >= 0) {
    const endpoints = calculateSnapLineEndpoints(
      false,
      bestVerticalSnap.value,
      bestVerticalSnap.sourceObj,
      target,
    );
    showSnapLine(canvas, endpoints.start, endpoints.end, false, true);

    state.pendingResizeSnap = {
      value: bestVerticalSnap.value,
      edge: bestVerticalSnap.edge,
      direction: 'vertical',
    };
  }

  if (bestHorizontalSnap && resizingEdges.vertical >= 0) {
    const endpoints = calculateSnapLineEndpoints(
      true,
      bestHorizontalSnap.value,
      bestHorizontalSnap.sourceObj,
      target,
    );
    showSnapLine(canvas, endpoints.start, endpoints.end, true, true);

    // Horizontal snap takes precedence if both exist
    state.pendingResizeSnap = {
      value: bestHorizontalSnap.value,
      edge: bestHorizontalSnap.edge,
      direction: 'horizontal',
    };
  }

  target.setCoords();
  canvas.renderAll();

  return bestVerticalSnap !== null || bestHorizontalSnap !== null;
};

/**
 * Apply resize snap when scaling ends
 */
export const applyResizeSnap = (e: TPointerEventInfo<TPointerEvent>, canvas: Canvas): boolean => {
  const { target } = e;
  const { pendingResizeSnap, activeResizeCorner } = state;

  if (!target || !pendingResizeSnap || !activeResizeCorner) {
    // Reset state and exit
    resetState(canvas);
    return false;
  }

  const resizingEdges = getResizingEdges(activeResizeCorner);
  if (!resizingEdges) {
    resetState(canvas);
    return false;
  }

  const targetSnapPoints = getObjectSnapPoints(target);
  let hasSnapped = false;

  // Apply the snap based on direction and resizing edge
  if (pendingResizeSnap.direction === 'horizontal' && resizingEdges.vertical >= 0) {
    // Apply horizontal snap (affects top or bottom edge)
    const snapOffsetY =
      pendingResizeSnap.value - targetSnapPoints.horizontal[resizingEdges.vertical];

    if (Math.abs(snapOffsetY) > 0.1) {
      const topDelta = snapOffsetY;
      const isTopEdge = resizingEdges.vertical === 0;
      const isBottomEdge = resizingEdges.vertical === 2;

      // Calculate new height based on which edge is being resized
      const newHeight = isTopEdge
        ? target.height - snapOffsetY / target.scaleY
        : isBottomEdge
          ? target.height + snapOffsetY / target.scaleY
          : target.height;

      if (isTopEdge || isBottomEdge) {
        target.set({
          height: newHeight,
          top: target.top + topDelta / 2,
        });
        hasSnapped = true;
      }
    }
  }

  if (pendingResizeSnap.direction === 'vertical' && resizingEdges.horizontal >= 0) {
    // Apply vertical snap (affects left or right edge)
    const snapOffsetX =
      pendingResizeSnap.value - targetSnapPoints.vertical[resizingEdges.horizontal];

    if (Math.abs(snapOffsetX) > 0.1) {
      const leftDelta = snapOffsetX;
      const isLeftEdge = resizingEdges.horizontal === 0;
      const isRightEdge = resizingEdges.horizontal === 2;

      // Calculate new width based on which edge is being resized
      const newWidth = isLeftEdge
        ? target.width - snapOffsetX / target.scaleX
        : isRightEdge
          ? target.width + snapOffsetX / target.scaleX
          : target.width;

      if (isLeftEdge || isRightEdge) {
        target.set({
          width: newWidth,
          left: target.left + leftDelta / 2,
        });
        hasSnapped = true;
      }
    }
  }

  // Update canvas if we applied a snap
  if (hasSnapped) {
    target.setCoords();
    canvas.requestRenderAll();
    target.fire('moving');
  }

  resetState(canvas);
  return hasSnapped;
};

/**
 * Reset the snapping state
 */
const resetState = (canvas: Canvas): void => {
  state.pendingResizeSnap = null;
  state.activeResizeCorner = null;
  clearSnapLines(canvas);
};

/**
 * Set up event handlers for resize snapping
 */
export const setupResizeSnappingHandlers = (
  canvas: Canvas,
  sideEffects?: {
    onResizeSnapEnd?: () => void;
  },
): void => {
  // Throttle settings
  const throttleTime = 16; // ~60fps
  let lastResizeTime = 0;

  const handleResizeEvent = (e: BasicTransformEvent<TPointerEvent> & { target: FabricObject }) => {
    const currentTime = Date.now();
    if (currentTime - lastResizeTime < throttleTime) return;
    lastResizeTime = currentTime;

    clearSnapLines(canvas);

    // Only show snap lines if Alt key is not pressed (Alt disables snapping)
    if (!e.e.altKey) {
      handleResizeSnap(e, canvas);
    }
  };

  // Handle resizing events
  canvas.on('object:resizing', handleResizeEvent);

  // Handle scaling events (but skip for images)
  canvas.on(
    'object:scaling',
    (e: BasicTransformEvent<TPointerEvent> & { target: FabricObject }) => {
      if (e.target.type.toLowerCase() === 'image') return;
      handleResizeEvent(e);
    },
  );

  // Apply snap when scaling/resizing ends
  canvas.on('mouse:up', (e) => {
    if (e.target && !e.e.altKey) {
      applyResizeSnap(e, canvas);
      sideEffects?.onResizeSnapEnd?.();
    }
    // Always clear snap lines when scaling ends
    clearSnapLines(canvas);
  });
};
